import React from "react"
import { useSelector } from "react-redux"

import { Tag } from "components/Tag"
import { AppState } from "store/AppState"
import { selectTagLabel } from "store/sharedSelectors/tagSelectors"
import { PKD } from "types/Template"

interface TagListProps {
  tags: number[]
  customTags: string[]
  activityFormCode?: number | null
  activityFormCodes?: number[]
  pkdCode?: PKD | null
  pkdCodes?: PKD[]
  pkds?: PKD[]
  logicalAlternativeTags?: number[]
  logicalAlternativeCustomTags?: string[]
  logicalAlternativePkds?: PKD[]
}

export const TagList = ({
  tags,
  customTags,
  activityFormCode,
  activityFormCodes,
  pkdCode,
  pkdCodes,
  pkds,
  logicalAlternativeTags,
  logicalAlternativeCustomTags,
  logicalAlternativePkds
}: TagListProps) => {
  const activityForms =
    activityFormCodes || (activityFormCode ? [activityFormCode] : [])

  const pkdList = pkds || pkdCodes || (pkdCode ? [pkdCode] : [])

  return (
    <div className="flex flex-wrap gap-1.5">
      {tags?.map((value) => (
        <TagElement key={`tag-${value}`} id={value} type="tag" />
      ))}
      {logicalAlternativeTags?.map((value) => (
        <TagElement key={`tag-${value}`} id={value} type="tag" isOptional />
      ))}

      {customTags?.map((value) => (
        <TagElement key={`customTags-${value}`} id={value} type="customTag" />
      ))}
      {logicalAlternativeCustomTags?.map((value) => (
        <TagElement
          key={`customTags-${value}`}
          id={value}
          type="customTag"
          isOptional
        />
      ))}

      {activityForms?.map((value) => (
        <TagElement
          key={`activity-form-${value}`}
          id={value}
          type="activityForm"
        />
      ))}

      {pkdList?.map(({ value, version }) => (
        <TagElement
          key={`pkd-${value}-${version}`}
          id={value}
          version={version}
          type="pkdCode"
        />
      ))}
      {logicalAlternativePkds?.map(({ value, version }) => (
        <TagElement
          key={`pkd-${value}-${version}`}
          id={value}
          version={version}
          type="pkdCode"
          isOptional
        />
      ))}
    </div>
  )
}

interface TagElementProps {
  id: number | string
  type: "tag" | "activityForm" | "pkdCode" | "customTag"
  version?: number // Required for pkd tags (2025 or 2007)
  isOptional?: boolean
}

const TagElement: React.FC<TagElementProps> = ({
  id,
  type,
  version,
  isOptional = false
}) => {
  const label = useSelector((s: AppState) => selectTagLabel(s, id, type))

  if (!label) {
    return null
  }

  return (
    <Tag
      formType="news"
      checked={!isOptional}
      isIndeterminate={isOptional}
      tooltip={
        type === "pkdCode"
          ? `${id}${version ? ` (${version}) - ${label}` : ""}`
          : ""
      }
    >
      {type === "pkdCode" ? `${id}${version ? ` (${version})` : ""}` : label}
    </Tag>
  )
}
